import { TFunction } from 'i18next';

export type FormatParams = {
  plural?: boolean;
  lower?: boolean;
};

export function getUnitLabel(
  t: TFunction,
  formatParams?: FormatParams
): string {
  const unitLabel = t('content.unitLabel', {
    count: formatParams?.plural ? 2 : 1,
  }).toString();
  return formatParams?.lower ? unitLabel.toLowerCase() : unitLabel;
}

export function getUnitProgramLabel(
  t: TFunction,
  formatParams?: FormatParams
): string {
  const unitProgramLabel = t('content.unitProgramLabel', {
    count: formatParams?.plural ? 2 : 1,
  }).toString();
  return formatParams?.lower
    ? unitProgramLabel.toLowerCase()
    : unitProgramLabel;
}

export function getFocusLabel(
  t: TFunction,
  formatParams?: FormatParams
): string {
  const focusLabel = t('content.focusLabel', {
    count: formatParams?.plural ? 2 : 1,
  }).toString();
  return formatParams?.lower ? focusLabel.toLowerCase() : focusLabel;
}

export function getKeyInquiryQuestionLabel(
  t: TFunction,
  formatParams?: FormatParams
): string {
  const label = t('content.keyInquiryQuestionLabel', {
    count: formatParams?.plural ? 2 : 1,
  }).toString();
  return formatParams?.lower ? label.toLowerCase() : label;
}

export function getOutcomesLabel(
  t: TFunction,
  formatParams?: FormatParams
): string {
  const label = t('content.outcomeLabel', {
    count: formatParams?.plural ? 2 : 1,
  }).toString();
  return formatParams?.lower ? label.toLowerCase() : label;
}

export function getPointLabel(
  t: TFunction,
  formatParams?: FormatParams
): string {
  const label = t('content.pointLabel', {
    count: formatParams?.plural ? 2 : 1,
  }).toString();
  if (formatParams?.lower === false) {
    return label;
  }
  return label.toLowerCase();
}

export function getUnitQuizLabel(
  t: TFunction,
  formatParams?: FormatParams
): string {
  const label = t('content.unitQuizLabel', {
    count: formatParams?.plural ? 2 : 1,
  }).toString();
  return formatParams?.lower ? label.toLowerCase() : label;
}

export function getUnitQuizDescription(t: TFunction): string {
  return t('content.unitQuizDescription');
}

type LabelGetter = (t: TFunction, formatParams?: FormatParams) => string;

// Every one of these labels resolves the same way: read the key, take the plural
// form when asked for, then lower-case it for mid-sentence use. Pluralised keys
// exist only as `_one`/`_other` variants, so they have to be given a count —
// passing one to a key without those variants would miss it.
const contentLabel =
  (
    key: string,
    { pluralised = false }: { pluralised?: boolean } = {}
  ): LabelGetter =>
  (t, formatParams) => {
    const label = t(
      `content.${key}`,
      pluralised ? { count: formatParams?.plural ? 2 : 1 } : undefined
    ).toString();
    return formatParams?.lower ? label.toLowerCase() : label;
  };

/** "Markbook" in AU, "Gradebook" in US. */
export const getMarkbookLabel = contentLabel('markbookLabel');
export const getAutomarkLabel = contentLabel('automarkLabel');
export const getAutomarkingLabel = contentLabel('automarkingLabel');
export const getAutomarkableLabel = contentLabel('automarkableLabel');
export const getAutomarkedLabel = contentLabel('automarkedLabel');
export const getMarkingLabel = contentLabel('markingLabel');
export const getMarkedLabel = contentLabel('markedLabel');

/*
 * AU says "mark" for three separate things that US names differently, so these
 * three are not interchangeable:
 *   getMarkVerbLabel  - the action.            AU "Mark"  / US "Grade"
 *   getMarkLabel      - the result achieved.   AU "Mark"  / US "Score"
 *   getPointLabel     - what a question is     AU "Mark"  / US "Point"
 *                       worth (declared above).
 * Picking the wrong one reads correctly in AU and wrongly in US.
 */

// Pass { lower: true } when the verb appears mid-sentence.
export const getMarkVerbLabel = contentLabel('markVerbLabel');
export const getMarkLabel = contentLabel('markLabel', { pluralised: true });

/** The grade range itself: "A–E" in AU, "4+ to 1" in US. */
export const getMarkScaleLabel = contentLabel('markScaleLabel');
